import React, { useState, useEffect, useRef } from "react";
import {
  Heart,
  CircleUser,
  ShoppingCart,
  User,
  LogOut,
  Search,
} from "lucide-react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import MegamenuDuo from "./MegamenuDuo";
import WishlistModal from "../Wishlist/WishlistModal";
import "../../assets/styles/components/Navbars/OtherTopnav.css";
import LoginPromptModal from "../LoginModal/LoginPromptModal";
import { useCartWishlist } from "../../app/CartWishlistContext";
import API from "../../app/api";
import { IoLogoWhatsapp } from "react-icons/io";
import searchicon from "../../assets/icons/Searchicon.svg";
import Desktoplogo from "../../assets/images/logo-dark.png";
import { getProductPdpUrl } from "../../utils/url.utils";

const OtherTopnav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { countData, getCartWishlistCount } = useCartWishlist();

  const [showMegaMenu, setShowMegaMenu] = useState(false);
  const [showWishlist, setShowWishlist] = useState(false);
  const [showUserPopup, setShowUserPopup] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [disableHover, setDisableHover] = useState(false);
  const [query, setQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [searchData, setSearchData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [chatData, setChatData] = useState(null);
  const userWrapperRef = useRef(null);
  const inputRef = useRef(null);
  const closeTimer = useRef(null);
  const isLoginPage = location.pathname === "/login";

  const [activeIndex, setActiveIndex] = useState(-1);
  const visibleResults = Array.isArray(searchData)
    ? searchData.slice(0, 8)
    : [];

useEffect(() => {
  if (activeIndex < 0) return;

  const el = document.querySelector(".active-item");
  if (el) {
    el.scrollIntoView({ block: "nearest" });
  }
}, [activeIndex]);


  // ------------------ Fetch Chat Support ------------------ //
  useEffect(() => {
    const fetchChatSupport = async () => {
      try {
        const res = await API.get("/chat/support");
        if (res.data.status === 200) {
          setChatData(res.data.data);
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchChatSupport();
  }, []);

  // ------------------ Session Check ------------------ //
  useEffect(() => {
    const token = localStorage.getItem("token");
    setIsLoggedIn(!!token);
  }, []);

  // ------------------ Close User Popup ------------------ //
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userWrapperRef.current && !userWrapperRef.current.contains(event.target)) {
        setShowUserPopup(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ------------------ Handle Search ------------------ //
  useEffect(() => {
    if (!query.trim()) {
      setSearchData([]);
      return;
    }
    setLoading(true);
    const handleSearch = async () => {
      try {
        const res = await API.get(`search?q=${query}`);
        if (res.data.status === 200) {
          setSearchData(res.data.data.products || []);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    handleSearch();
  }, [query]);

  // ------------------ Focus Input & Prevent Scroll ------------------ //
  useEffect(() => {
    if (showSearch) {
      inputRef.current?.focus();
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [showSearch]);

  // ------------------ Cart / Wishlist Click ------------------ //
  const handleCartClick = (e) => {
    const token = localStorage.getItem("token");
    if (!token) {
      e.preventDefault();
      navigate("/login", { state: { redirectTo: "/cart" } });
    }
  };

  const handleWishlistClick = () => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login", { state: { from: location.pathname } });
    } else {
      setShowWishlist(true);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("userName");
    setIsLoggedIn(false);
    setShowUserPopup(false);
    getCartWishlistCount();
    navigate("/");
  };

  const handleUserClick = (e) => {
    e.stopPropagation();
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
    } else {
      setShowUserPopup((prev) => !prev);
    }
  };

  const clearSearch = () => {
    setQuery("");
    setSearchData([]);
    setShowSearch(false);
  };

  // ------------------ MegaMenu Hover ------------------ //
  const handleMouseEnter = () => {
    if (disableHover) return;
    clearTimeout(closeTimer.current);
    setShowMegaMenu(true);
  };

  const handleMouseLeave = () => {
    if (disableHover) return;
    closeTimer.current = setTimeout(() => setShowMegaMenu(false), 200);
  };

  const handleItemClick = () => {
    clearTimeout(closeTimer.current);
    setDisableHover(true);
    setShowMegaMenu(false);
    setTimeout(() => setDisableHover(false), 200);
  };

  // ------------------ Highlight Function ------------------ //
  const highlightText = (text, query) => {
    const regex = new RegExp(`(${query})`, "gi");
    return text.replace(regex, "<mark>$1</mark>");
  };

  return (
    <>
      <nav className="other-topnav">
        <div className="nav-logo" style={{ cursor: "pointer" }}>
          <Link to="/">
            <img src={Desktoplogo} alt="Obsessions" />
          </Link>
        </div>

        <ul className="nav-links">
          <li
            className="shop-wrapper"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <div className="track_poster">
              <span style={{ cursor: "pointer" }}>SHOP</span>
            </div>
            <div className={`megamenu-wrapper ${showMegaMenu ? "visible" : ""}`}>
              <MegamenuDuo closeMenu={handleItemClick} />
            </div>
          </li>

          <li>
            <NavLink to="/shop/new-arrivals" className={({ isActive }) => (isActive ? "active-tab" : "")}>
              <div className="track_poster">NEW ARRIVALS</div>
            </NavLink>
          </li>
          <li>
            <NavLink to="/shop/bestsellers" className={({ isActive }) => (isActive ? "active-tab" : "")}>
              <div className="track_poster">BESTSELLERS</div>
            </NavLink>
          </li>
          <li>
            <NavLink to="/shop/offer-spot" className={({ isActive }) => (isActive ? "active-tab" : "")}>
              <div className="track_poster">OFFERS SPOT</div>
            </NavLink>
          </li>
          <li>
            <NavLink to="/floor-matcher" className={({ isActive }) => (isActive ? "active-tab" : "")}>
              <div className="track_poster">FLOOR MATCHER</div>
            </NavLink>
          </li>
        </ul>

        <div className="nav-actions">
          {/* Search Icon */}
          <img
            src={searchicon}
            alt="search"
            onClick={() => setShowSearch(true)}
            style={{ cursor: "pointer" }}
          />

          {/* User Icon */}
          
          <div ref={userWrapperRef} className="user-click-wrapper" style={{ position: "relative", left: '7px' }}>
  <span style={{ fontSize: "12px", paddingRight: "7px" }}>
    {localStorage.getItem('userName')?.split(' ')[0] || ''}
  </span>

  {/* User Icon */}
  <CircleUser
    strokeWidth={1}
    color="#090909"
    size={25}
    style={{ cursor: "pointer" }}
    onClick={(e) => {
      e.stopPropagation();          // prevent outside click from closing
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/login");         // go to login if not logged in
      } else {
        setShowUserPopup(prev => !prev);  // toggle dropdown
      }
    }}
  />

  {/* Dropdown */}
  {showUserPopup && (
    <>
      <div className="popup-triangle"></div>
      <div className="user-popup">
        <div className="popup-item">
          <Link
            to="/accounts/profile"
            onClick={() => setShowUserPopup(false)} // auto-close dropdown
          >
            <User size={22} style={{ marginRight: 8 }} />
            Profile
          </Link>
        </div>
        <div className="popup-item" onClick={() => {
          handleLogout();                     // logout function already closes dropdown
        }}>
          <LogOut size={22} style={{ marginRight: 8, transform: "rotate(180deg)" }} />
          Logout
        </div>
      </div>
    </>
  )}
          </div>

          {/* Wishlist */}
          <div title="Wishlist" onClick={handleWishlistClick} style={{ cursor: "pointer" }}>
            <Heart
              strokeWidth={1}
              size={25}
              title="Wish List"
            />
            <span className="wishlist-badge" style={{ right: "68px", top: "10px", color: "black" }}>
              <span style={{ position: 'relative', top: '1px' }}> {countData?.wishlist_count ?? ''}</span>
            </span>
          </div>

          {/* Cart */}
          <div title="Cart">
            <Link to="/cart" onClick={handleCartClick}>
              <ShoppingCart
                onClick={handleCartClick}
                strokeWidth={1}
                title="Shopping Cart"
                size={25}
                style={{ cursor: "pointer" }}
              />
              <span className="wishlist-badge" style={{ right: "27px", top: "10px", color: "black" }}>
                <span style={{ position: 'relative', top: '1px' }}>{countData?.cart_count ?? ''} </span>
              </span>
            </Link>
          </div>
        </div>
      </nav>

      <div style={{ height: "50px" }}></div>

      {/* Wishlist & Login Modals */}
      {showWishlist && <WishlistModal onClose={() => setShowWishlist(false)} />}
      {showLoginPrompt && !isLoginPage && <LoginPromptModal onClose={() => setShowLoginPrompt(false)} />}

      {/* Fullscreen Search */}
      {showSearch && (
        <div className="search-overlay" onClick={clearSearch}>
          <div className="search-modal-other" onClick={(e) => e.stopPropagation()}>
            <div className="d-flex position-relative">
              <input
                ref={inputRef}
                className="form-control border-0 input_home"
                placeholder="WHAT ARE YOU LOOKING FOR?"
                value={query}
                onChange={(e) => setQuery(e.target.value.replace(/[^a-zA-Z0-9 ]/g, "").replace(/^\s+/, ""))}
               onKeyDown={(e) => {
    if (!visibleResults.length && e.key !== "Enter") return;

    //  DOWN
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) =>
        prev < visibleResults.length - 1 ? prev + 1 : 0
      );
    }

    // UP
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) =>
        prev > 0 ? prev - 1 : visibleResults.length - 1
      );
    }

    //  ENTER
    if (e.key === "Enter") {
      e.preventDefault();

      const trimmed = query.trim();

      if (activeIndex >= 0 && visibleResults[activeIndex]) {
        const item = visibleResults[activeIndex];
        clearSearch();
      } else if (trimmed) {
        navigate(`/search?q=${encodeURIComponent(trimmed)}`);
        clearSearch();
      }
    }
  }}
              />
              <button
                  type="button"
                  className="btn btn-dark button_search"
                  disabled={!query.trim()}
                  onClick={() => {
                    const trimmed = query.trim();
                    if (!trimmed) return;
                    navigate(`/search?q=${encodeURIComponent(trimmed)}`);
                    clearSearch();
                  }}
                >
                  <Search strokeWidth={1.25} />
                </button>

             {query.trim() && (
  <div className="autocomplete-box">
    {loading && <div className="p-3 text-center">Searching...</div>}

    {!loading && visibleResults.length === 0 && (
      <div className="p-3 text-center">No results found</div>
    )}

    {!loading &&
      visibleResults.map((item, i) => (
        <Link
          key={item.id}
          to={getProductPdpUrl(item.action_url, item.sku)}
          className={`autocomplete-item ${
            i === activeIndex ? "active-item" : ""
          }`}
          onMouseEnter={() => setActiveIndex(i)}
          onClick={clearSearch}
        >
          <img
            src={item.media_list?.main?.file}
            alt={item.name}
            className="autocomplete-img"
          />

          <span
            className="autocomplete-text"
            dangerouslySetInnerHTML={{
              __html: highlightText(item.name, query),
            }}
          ></span>
        </Link>
      ))}
  </div>
)}
            </div>
          </div>
        </div>
      )}

      {/* Floating WhatsApp */}
      {chatData && (
        <a
          href={`https://api.whatsapp.com/send?phone=${chatData.phone}&text=${chatData.text}`}
          className="floating-whatsapp"
          target="_blank"
          rel="noopener noreferrer"
        >
          <IoLogoWhatsapp className="whatsapp-icon" />
        </a>
      )}
    </>
  );
};

export default OtherTopnav;